import React, { useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useChat } from '../../context/ChatContext';
import { msgsAPI } from '../../utils/api';
import { formatTimeOnly as formatTime, formatFileSize, isImageFile, isVideoFile, isAudioFile } from '../../utils/helpers';
import Avatar from '../UI/Avatar';

const EMOJIS = ['❤️', '😂', '😮', '😢', '🙏', '👍'];

export default function MessageBubble({ msg, convId, conversations, onReply, onEdit, onReact, onUnreact, isGroup }) {
  const { user }                        = useAuth();
  const { deleteMessage }               = useChat();
  const [showMenu, setShowMenu]         = useState(false);
  const [menuPos, setMenuPos]           = useState({ x: 0, y: 0 });
  const [showEmojis, setShowEmojis]     = useState(false);
  const [editing, setEditing]           = useState(false);
  const [editText, setEditText]         = useState(msg.content || '');
  const [showForward, setShowForward]   = useState(false);

  const isMe = msg.sender_id === user?.id;
  // Blue ticks: only show if at least one other participant has read it
  const isRead = (msg.read_count || 0) > 0;
  // System messages (add/leave group) shown differently
  const isSystem = !isGroup && false; // handled below

  const handleContextMenu = (e) => {
    e.preventDefault();
    setMenuPos({ x: e.clientX, y: e.clientY });
    setShowMenu(true);
  };

  const handleReact = async (emoji) => {
    await onReact(convId, msg.id, emoji);
    setShowEmojis(false);
    setShowMenu(false);
  };

  const handleUnreact = async () => {
    await onUnreact(convId, msg.id);
  };

  const handleEdit = async () => {
    if (!editText.trim()) return;
    await onEdit(convId, msg.id, editText);
    setEditing(false);
  };

  const handleDelete = async () => {
    await deleteMessage(convId, msg.id);
    setShowMenu(false);
  };

  const handleDownloadImage = (url) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = url.split('/').pop() || 'image';
    a.target = '_blank';
    a.click();
  };

  // System message display (group join/leave)
  if (isGroup && msg.sender_id && msg.type === 'text' &&
      (msg.content?.includes(' added ') || msg.content?.includes(' left the group') || msg.content?.includes('Added '))) {
    return (
      <div style={{ textAlign: 'center', margin: '8px 0' }}>
        <span style={{ background: 'rgba(0,0,0,0.08)', borderRadius: 12, padding: '4px 14px', fontSize: 12, color: '#667781' }}>
          {msg.content}
        </span>
      </div>
    );
  }

  const renderContent = () => {
    if (msg.is_deleted) return <p className="msg-deleted">🚫 This message was deleted</p>;
    if (editing) return (
      <div style={{ minWidth: 180 }}>
        <input
          style={{ width: '100%', padding: '4px 6px', border: '1px solid #ccc', borderRadius: 4, fontSize: 14, boxSizing: 'border-box' }}
          value={editText}
          onChange={e => setEditText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleEdit(); if (e.key === 'Escape') setEditing(false); }}
          autoFocus
        />
        <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
          <button onClick={handleEdit} className="btn btn-primary btn-sm" style={{ flex: 1 }}>Save</button>
          <button onClick={() => setEditing(false)} className="btn btn-ghost btn-sm" style={{ flex: 1 }}>Cancel</button>
        </div>
      </div>
    );

    if (msg.file_url) {
      if (isImageFile(msg.file_url) || msg.type === 'image') return (
        <div>
          {msg.content && <p className="msg-text" style={{ marginBottom: 4 }}>{msg.content}</p>}
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <img src={msg.file_url} alt="img" className="msg-image"
              onClick={() => window.open(msg.file_url, '_blank')} style={{ cursor: 'pointer' }} />
            <button
              onClick={() => handleDownloadImage(msg.file_url)}
              title="Download photo"
              style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 14 }}>
              ⬇
            </button>
          </div>
        </div>
      );
      if (isVideoFile(msg.file_url) || msg.type === 'video') return (
        <video src={msg.file_url} className="msg-video" controls />
      );
      if (isAudioFile(msg.file_url) || msg.type === 'audio' || msg.type === 'voice') return (
        <div>
          <audio src={msg.file_url} className="msg-audio" controls preload="metadata" />
        </div>
      );
      return (
        <a href={msg.file_url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
          <div className="msg-file">
            <span className="msg-file-icon">📄</span>
            <div className="msg-file-info">
              <div className="msg-file-name">{msg.file_name || 'File'}</div>
              <div className="msg-file-size">{formatFileSize(msg.file_size)}</div>
            </div>
            <span style={{ color: 'var(--primary)', fontSize: 20 }}>⬇️</span>
          </div>
        </a>
      );
    }

    return <p className="msg-text">{msg.content}</p>;
  };

  // Group reactions
  const reactionMap = {};
  (msg.reactions || []).forEach(r => {
    if (!reactionMap[r.emoji]) reactionMap[r.emoji] = { emoji: r.emoji, count: 0, mine: false };
    reactionMap[r.emoji].count++;
    if (r.user_id === user?.id) reactionMap[r.emoji].mine = true;
  });

  return (
    <>
      <div className={`message-row ${isMe ? 'out' : 'in'}`} onContextMenu={handleContextMenu}>
        {!isMe && isGroup && (
          <div className="msg-avatar">
            <Avatar user={{ name: msg.sender_name, avatar: msg.sender_avatar }} size={28} />
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start', maxWidth: '65%', minWidth: 0 }}>
          {/* Reply preview */}
          {msg.reply_message && (
            <div className="reply-preview" style={{ maxWidth: '100%' }}>
              <div className="sender">{msg.reply_message.name}</div>
              <div className="text">{msg.reply_message.content || `[${msg.reply_message.type}]`}</div>
            </div>
          )}

          <div className="message-bubble"
            onMouseEnter={() => !msg.is_deleted && setShowEmojis(true)}
            onMouseLeave={() => setShowEmojis(false)}
            style={{ position: 'relative' }}>
            {!isMe && isGroup && <div className="msg-sender">{msg.sender_name}</div>}

            {renderContent()}

            <div className="msg-meta">
              {msg.is_edited == 1 && !msg.is_deleted && <span className="msg-edited">edited</span>}
              <span className="msg-time">{formatTime(msg.created_at)}</span>
              {isMe && !msg.is_deleted && (
                <span className="msg-ticks" style={{ color: isRead ? '#53BDEB' : undefined }}>
                  {isRead ? '✓✓' : '✓'}
                </span>
              )}
            </div>

            {/* Quick emoji bar on hover */}
            {showEmojis && !msg.is_deleted && (
              <div style={{
                position: 'absolute', [isMe ? 'left' : 'right']: '0', top: '-38px',
                background: 'white', borderRadius: 20, padding: '4px 8px',
                display: 'flex', gap: 4, boxShadow: '0 2px 12px rgba(0,0,0,.15)', zIndex: 50,
              }}>
                {EMOJIS.map(e => (
                  <button key={e} onClick={() => handleReact(e)}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 18, padding: '2px', borderRadius: '50%', transition: 'transform .1s' }}
                    onMouseEnter={el => el.target.style.transform = 'scale(1.3)'}
                    onMouseLeave={el => el.target.style.transform = 'scale(1)'}
                  >{e}</button>
                ))}
              </div>
            )}
          </div>

          {/* Reactions */}
          {Object.keys(reactionMap).length > 0 && (
            <div className="msg-reactions">
              {Object.values(reactionMap).map(r => (
                <button key={r.emoji} className="reaction-pill"
                  onClick={() => r.mine ? handleUnreact() : handleReact(r.emoji)}
                  style={{ border: r.mine ? '1px solid var(--primary)' : undefined }}>
                  {r.emoji} <span className="count">{r.count}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Context Menu */}
      {showMenu && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 499 }} onClick={() => setShowMenu(false)} />
          <div className="context-menu" style={{ position: 'fixed', left: menuPos.x, top: menuPos.y, zIndex: 500 }}>
            <div className="context-menu-item" onClick={() => { onReply(msg); setShowMenu(false); }}>↩️ Reply</div>
            {isMe && !msg.is_deleted && (
              <div className="context-menu-item" onClick={() => { setEditing(true); setShowMenu(false); }}>✏️ Edit</div>
            )}
            <div className="context-menu-item" onClick={() => { setShowForward(true); setShowMenu(false); }}>↪️ Forward</div>
            {(msg.type === 'image' || msg.type === 'voice' || msg.type === 'audio') && msg.file_url && (
              <div className="context-menu-item" onClick={() => { handleDownloadImage(msg.file_url); setShowMenu(false); }}>⬇️ Download</div>
            )}
            <div className="context-menu-item" onClick={() => { navigator.clipboard.writeText(msg.content || ''); setShowMenu(false); }}>📋 Copy</div>
            {/* Emoji reactions in menu */}
            <div style={{ display: 'flex', gap: 4, padding: '8px 12px', borderTop: '1px solid #f0f0f0' }}>
              {EMOJIS.map(e => (
                <button key={e} onClick={() => handleReact(e)}
                  style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 18 }}>{e}</button>
              ))}
            </div>
            {isMe && (
              <div className="context-menu-item danger" onClick={handleDelete}>🗑️ Delete</div>
            )}
          </div>
        </>
      )}

      {/* Forward Modal */}
      {showForward && (
        <ForwardModal
          msg={msg}
          conversations={conversations}
          onClose={() => setShowForward(false)}
        />
      )}
    </>
  );
}

function ForwardModal({ msg, conversations, onClose }) {
  const [selected, setSelected] = useState([]);
  const [forwarding, setForwarding] = useState(false);
  const [done, setDone] = useState(false);

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const handleForward = async () => {
    if (selected.length === 0) return;
    setForwarding(true);
    try {
      await msgsAPI.forward(msg.id, selected);
      setDone(true);
      setTimeout(onClose, 1200);
    } catch {}
    setForwarding(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>↪️ Forward Message</h2>
          <button className="icon-btn dark" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ maxHeight: 320, overflowY: 'auto' }}>
          {done ? (
            <div style={{ textAlign: 'center', padding: 24, color: 'var(--primary)', fontWeight: 600 }}>✅ Forwarded!</div>
          ) : conversations.map(c => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 4px', cursor: 'pointer', borderRadius: 8 }}
              onClick={() => toggle(c.id)}>
              <div style={{ width: 22, height: 22, borderRadius: '50%', border: `2px solid ${selected.includes(c.id) ? 'var(--primary)' : '#ccc'}`, background: selected.includes(c.id) ? 'var(--primary)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 14, flexShrink: 0 }}>
                {selected.includes(c.id) ? '✓' : ''}
              </div>
              <span style={{ fontSize: 14, fontWeight: 500 }}>{c.name}</span>
            </div>
          ))}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleForward} disabled={forwarding || selected.length === 0}>
            {forwarding ? '⏳' : `Forward (${selected.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}
